
import { useState, useEffect } from 'react'
import { Conversation } from '../../../hooks/useMessages'

interface GroupSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  conversation: Conversation
  onUpdateSettings: (name: string, icon: string) => Promise<void>
}

const iconOptions = [
  'ri-group-line',
  'ri-team-line',
  'ri-movie-2-line',
  'ri-film-line',
  'ri-star-line',
  'ri-heart-line',
  'ri-chat-smile-3-line',
  'ri-gamepad-line',
  'ri-music-2-line',
  'ri-book-open-line',
  'ri-cup-line',
  'ri-fire-line'
]

const GroupSettingsModal = ({ isOpen, onClose, conversation, onUpdateSettings }: GroupSettingsModalProps) => {
  const [groupName, setGroupName] = useState('')
  const [groupIcon, setGroupIcon] = useState('ri-group-line')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen && conversation) {
      setGroupName(conversation.group_name || '')
      setGroupIcon(conversation.group_icon || 'ri-group-line')
      setError('')
    }
  }, [isOpen, conversation])

  const handleSave = async () => {
    if (!groupName.trim()) {
      setError('Please enter a group name')
      return
    }

    setSaving(true)
    setError('')
    try {
      await onUpdateSettings(groupName.trim(), groupIcon)
      onClose()
    } catch (err) {
      console.error('Error updating group:', err)
      setError('Failed to update group settings')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Group Settings</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-gray-500"></i>
          </button>
        </div>

        <div className="p-5">
          {/* Group Icon Preview */}
          <div className="flex justify-center mb-5">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white">
              <i className={`${groupIcon} text-3xl`}></i>
            </div>
          </div>

          {/* Icon Selection */}
          <div className="mb-5">
            <label className="block text-xs font-medium text-gray-500 mb-2">Choose Icon</label>
            <div className="flex flex-wrap gap-2 justify-center">
              {iconOptions.map((icon) => (
                <button
                  key={icon}
                  onClick={() => setGroupIcon(icon)}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all cursor-pointer ${
                    groupIcon === icon
                      ? 'bg-purple-100 text-purple-600 ring-2 ring-purple-500'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${icon} text-xl`}></i>
                </button>
              ))}
            </div>
          </div>

          {/* Group Name */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-2">Group Name</label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name..."
              maxLength={50}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{groupName.length}/50</p>
          </div>

          {error && (
            <p className="text-xs text-red-500 mb-4 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              {error}
            </p>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium rounded-xl hover:shadow-lg transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GroupSettingsModal
